// src/pages/categorias/ListaCategorias.js
import React, { useState, useEffect, useCallback } from 'react'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Table from 'react-bootstrap/Table'
import DropdownButton from 'react-bootstrap/DropdownButton'
import ButtonGroup from 'react-bootstrap/ButtonGroup'
import Dropdown from 'react-bootstrap/Dropdown'
import { LoadingSpinner } from 'components/LoadingSpinner'
import Paginacion from 'components/Paginacion'
import { CATEGORIAS_NO_IMAGE } from 'config/categorias'
import { API_URL } from 'config/general'

import './categorias.css'

// Determina el color para cada nivel jerárquico
const LEVEL_COLORS = ['#6610f2', '#007bff', '#17a2b8', '#28a745', '#fd7e14', '#dc3545']
const getLevelColor = (level) => LEVEL_COLORS[level % LEVEL_COLORS.length]

export default function ListaCategorias(props) {
	const {
		loading,
		categorias,
		numResultados,
		fetchDeACuantos,
		pagina,
		cambiarPagina,
		acciones,
		categoriasActualizando = [] // Nueva prop con valor por defecto
	} = props

	const [expandedCategories, setExpandedCategories] = useState([])
	const [draggedItem, setDraggedItem] = useState(null)
	const [dragOverItem, setDragOverItem] = useState(null)

	// Cargar imagen por defecto para pre-cargarla en el navegador
	useEffect(() => {
		const img = new Image()
		img.src = CATEGORIAS_NO_IMAGE
	}, [])

	// Expandir categorías principales por defecto al cargar
	useEffect(() => {
		if (categorias && categorias.length > 0) {
			const mainCategoryIds = categorias.map(c => c.id)
			setExpandedCategories(prev => {
				// Mantener las categorías expandidas que aún existen
				const existingIds = prev.filter(id =>
					mainCategoryIds.includes(id) || categorias.some(cat =>
						cat.subcategorias?.some(subcat => subcat.id === id)
					)
				)

				// Agregar categorías principales que no estén ya expandidas
				const newIds = mainCategoryIds.filter(id => !existingIds.includes(id))

				return [...existingIds, ...newIds]
			})
		}
	}, [categorias])

	const toggleExpand = useCallback((categoryId, e) => {
		e.stopPropagation()
		setExpandedCategories(prev => {
			if (prev.includes(categoryId)) {
				return prev.filter(id => id !== categoryId)
			} else {
				return [...prev, categoryId]
			}
		})
	}, [])

	const handleDragStart = useCallback((e, category) => {
		// No permitir arrastrar si hay categorías actualizándose
		if (categoriasActualizando.length > 0) {
			e.preventDefault()
			return
		}

		// Guardar la categoría que estamos arrastrando
		setDraggedItem(category)

		// Aplicar estilo de "siendo arrastrado"
		e.currentTarget.style.opacity = '0.6'
		e.currentTarget.classList.add('dragging')

		// Definir el efecto permitido
		e.dataTransfer.effectAllowed = 'move'
	}, [categoriasActualizando])

	const handleDragOver = useCallback((e, category) => {
		// Siempre prevenir el comportamiento predeterminado
		e.preventDefault()

		// Si no hay elemento arrastrado, salir
		if (!draggedItem) return

		// Actualizar el elemento sobre el que estamos
		setDragOverItem(category)

		// Aplicar el cursor correcto según si es el mismo elemento o no
		if (draggedItem.id === category.id) {
			// Es el mismo elemento - indicar cancelación
			e.currentTarget.style.cursor = 'not-allowed'
			e.currentTarget.classList.add('self-drop-target')
			e.currentTarget.classList.remove('drop-target')
		} else {
			// Es un elemento diferente - indicar que se puede soltar
			e.currentTarget.style.cursor = 'grabbing'
			e.currentTarget.classList.add('drop-target')
			e.currentTarget.classList.remove('self-drop-target')
		}

		// Dejar claro que el elemento puede recibir un drop
		e.dataTransfer.dropEffect = 'move'
	}, [draggedItem])

	const handleDragLeave = useCallback((e) => {
		// Limpiar todas las clases y estilos específicos de arrastre
		e.currentTarget.classList.remove('drop-target')
		e.currentTarget.classList.remove('self-drop-target')
		e.currentTarget.style.cursor = ''
	}, [])

	const handleDragEnd = useCallback((e) => {
		// Restaurar opacidad normal
		e.currentTarget.style.opacity = '1'

		// Restaurar estilo de cursor normal
		e.currentTarget.style.cursor = 'grab'

		// Eliminar todas las clases relacionadas con arrastre
		e.currentTarget.classList.remove('dragging')
		e.currentTarget.classList.remove('drop-target')
		e.currentTarget.classList.remove('self-drop-target')

		// Limpiar los estados de arrastre
		setDraggedItem(null)
		setDragOverItem(null)

		// Para seguridad adicional, limpiar todos los cursores de otros elementos
		// que pudieran haber quedado modificados
		document.querySelectorAll('.categoria-row').forEach(row => {
			row.style.cursor = 'grab'
			row.classList.remove('drop-target')
			row.classList.remove('self-drop-target')
		})
	}, [])

	// Funciones auxiliares para el drag & drop
	const obtenerCategoriasMismoNivel = useCallback((category) => {
		if (!category.jerarquiaPapa || !category.jerarquiaPapa.id) {
			return categorias // Es categoría principal
		}

		// Buscar el padre y sus subcategorías
		const padre = encontrarCategoriaPorId(categorias, category.jerarquiaPapa.id)

		const subcats = padre?.subcategorias || []

		return subcats
	}, [categorias])

	const encontrarCategoriaPorId = useCallback((lista, id) => {
		for (const cat of lista) {
			if (cat.id === id) return cat

			if (cat.subcategorias && cat.subcategorias.length > 0) {
				const encontrado = encontrarCategoriaPorId(cat.subcategorias, id)
				if (encontrado) return encontrado
			}
		}
		return null
	}, [])

	const getLevel = useCallback((category) => {
		// Si no tiene padre, es una categoría principal (nivel 0)
		if (!category.jerarquiaPapa || !category.jerarquiaPapa.id) {
			return 0
		}

		// Para determinar el nivel correcto, necesitamos recorrer todos los padres
		let nivel = 1
		let categoriaActual = category

		// Mientras la categoría tenga un padre, seguimos subiendo en la jerarquía
		while (categoriaActual.jerarquiaPapa && categoriaActual.jerarquiaPapa.id) {
			// Si el padre tiene a su vez un padre, necesitamos buscarlo en la lista completa
			if (categoriaActual.jerarquiaPapa.jerarquiaPapa) {
				// Ya tenemos la información del padre completa, seguimos recorriendo
				categoriaActual = categoriaActual.jerarquiaPapa
				nivel++
			} else {
				// El padre no tiene información completa de su propio padre
				// Buscar la información completa del padre
				const padreDatos = encontrarCategoriaPorId(categorias, categoriaActual.jerarquiaPapa.id)

				if (!padreDatos || !padreDatos.jerarquiaPapa || !padreDatos.jerarquiaPapa.id) {
					// El padre es una categoría principal, terminamos
					break
				}

				// El padre tiene su propio padre, aumentamos el nivel y seguimos
				categoriaActual = padreDatos
				nivel++
			}
		}

		return nivel
	}, [categorias, encontrarCategoriaPorId])

	const esHijoDe = useCallback((posiblePadre, posibleHijo) => {
		if (!posiblePadre.subcategorias) return false

		for (const sub of posiblePadre.subcategorias) {
			if (sub.id === posibleHijo.id) return true
			if (esHijoDe(sub, posibleHijo)) return true
		}

		return false
	}, [])

	const handleDrop = useCallback((e, category) => {
		e.preventDefault()
		if (!draggedItem) return

		// Limpiar cursores y clases
		e.currentTarget.style.cursor = 'grab'
		e.currentTarget.classList.remove('drop-target')
		e.currentTarget.classList.remove('self-drop-target')

		// Si se suelta sobre sí mismo, cancelar la operación
		if (draggedItem.id === category.id) {
			setDraggedItem(null)
			setDragOverItem(null)
			return
		}

		// Determinar si es un reordenamiento o un cambio de padre
		const draggedLevel = getLevel(draggedItem)
		const dropLevel = getLevel(category)

		// Si el arrastrado tiene padre y el destino tiene padre, verificar si son el mismo
		const draggedPadreId = draggedItem.jerarquiaPapa?.id
		const dropPadreId = category.jerarquiaPapa?.id

		// Criterio modificado: mismo nivel Y mismo padre para reordenar
		// En cualquier otro caso, es una operación de mover
		const mismoNivelYPadre = draggedLevel === dropLevel && draggedPadreId === dropPadreId

		if (mismoNivelYPadre) {
			// Este es el array que enviamos al servidor
			const categoriasOrdenadas = []

			// Obtener todas las categorías del mismo nivel y padre
			const categoriasDelMismoNivel = obtenerCategoriasMismoNivel(draggedItem)

			// Estas son las dos categorías involucradas en el reordenamiento
			// La 1 es la que se mueve (arrastra)
			// La 2 es sobre la que se suelta (objetivo)
			const reordenamiento = [
				{ id: draggedItem.id },
				{ id: category.id }
			]

			// Crear nuevo orden para todas las categorías del nivel
			let newOrder = 1
			for (const cat of categoriasDelMismoNivel) {
				// Si es la categoría que estamos moviendo, la saltamos
				// porque la insertaremos en otra posición
				if (cat.id === draggedItem.id) continue

				// Añadir la categoría actual
				categoriasOrdenadas.push({
					id: cat.id,
					orden: newOrder++
				})

				// Si es la categoría objetivo, insertar la categoría arrastrada DESPUÉS
				if (cat.id === category.id) {
					categoriasOrdenadas.push({
						id: draggedItem.id,
						orden: newOrder++
					})
				}
			}

			// Si al final no se agregó la categoría arrastrada (e.g., es la última)
			if (!categoriasOrdenadas.some(c => c.id === draggedItem.id)) {
				categoriasOrdenadas.push({
					id: draggedItem.id,
					orden: newOrder
				})
			}

			// Asegurarnos de que la copia optimista reciba la información necesaria
			// para ubicar correctamente los elementos visualmente
			acciones.handleReordenar(categoriasOrdenadas, reordenamiento)
		} else {
			// Mover a otro padre
			let nuevoPadreId
			let nuevoOrden

			// Si es una categoría principal, el nuevo padre es null (pasa a ser categoría principal)
			if (!category.jerarquiaPapa || !category.jerarquiaPapa.id) {
				nuevoPadreId = null
				nuevoOrden = categorias.length + 1 // Ponerla al final de las categorías principales
			} else {
				// Si es una subcategoría, el nuevo padre es su padre
				nuevoPadreId = category.jerarquiaPapa.id

				// Buscar las categorías del mismo nivel que la categoría destino
				const hermanosDrop = obtenerCategoriasMismoNivel(category)

				// Calcular posición de inserción: DESPUÉS del elemento donde se soltó
				const posicionSoltar = hermanosDrop.findIndex(c => c.id === category.id)
				nuevoOrden = posicionSoltar + 2 // +1 por el índice basado en 0, +1 por insertar después
			}

			// Verificar que no estamos creando un ciclo
			const esPadreValido = !esHijoDe(draggedItem, category)

			if (esPadreValido) {
				acciones.handleMover(draggedItem.id, nuevoPadreId, nuevoOrden)
			}
		}

		// Limpiar estados de arrastre
		setDraggedItem(null)
		setDragOverItem(null)
	}, [draggedItem, getLevel, obtenerCategoriasMismoNivel, esHijoDe, acciones, categorias])

	// Función recursiva para renderizar categorías con cualquier nivel de anidamiento
	const renderCategory = useCallback((category, level = 0, path = []) => {
		const isExpanded = expandedCategories.includes(category.id)
		const hasSubcategories = category.subcategorias && category.subcategorias.length > 0
		const isDraggable = categoriasActualizando.length === 0 // Deshabilitar arrastre durante actualizaciones
		const isDropTarget = dragOverItem && dragOverItem.id === category.id
		const isSelfDrop = draggedItem && dragOverItem && draggedItem.id === dragOverItem.id && dragOverItem.id === category.id
		const levelColor = getLevelColor(level)
		const isUpdating = categoriasActualizando.includes(category.id)

		// Para el breadcrumb/ruta
		const currentPath = [...path, category.nombre]

		// Clase base para la fila
		let rowClassName = `categoria-row ${level === 0 ? "categoria-padre" : `categoria-nivel-${level}`}`

		// Añadir clase para categorías actualizándose
		if (isUpdating) {
			rowClassName += " categoria-actualizando"
		}

		return (
			<React.Fragment key={category.id}>
				<tr
					className={rowClassName}
					style={{ cursor: isDraggable ? 'grab' : 'default' }}
					draggable={isDraggable}
					onDragStart={(e) => handleDragStart(e, category)}
					onDragOver={(e) => handleDragOver(e, category)}
					onDragLeave={handleDragLeave}
					onDragEnd={handleDragEnd}
					onDrop={(e) => handleDrop(e, category)}
				>
					{/* Icono de arrastre */}
					<td className="text-center drag-handle-cell">
						<span className="drag-handle" title="Arrastrar para reordenar">
							<i className="fa fa-grip-vertical"></i>
						</span>
					</td>

					{/* ID */}
					<td className="text-center id-cell">
						{category.id}
					</td>

					{/* Nombre con indentación */}
					<td className="nombre-cell">
						<div className="d-flex align-items-center">
							{/* Líneas de indentación */}
							{level > 0 && (
								<div className="hierarchy-indentation" style={{ width: `${level * 20}px` }}>
									{Array.from({ length: level }).map((_, i) => (
										<div
											key={i}
											className="indentation-line"
											style={{
												left: `${i * 20}px`,
												borderLeftColor: i === level - 1 ? getLevelColor(i) : 'transparent'
											}}
										/>
									))}
								</div>
							)}

							{/* Indicador de categoría */}
							<div className="categoria-indicator" style={{ color: levelColor }}>
								{hasSubcategories ? (
									<span
										onClick={(e) => toggleExpand(category.id, e)}
										className="categoria-toggle"
									>
										<i className={`fa fa-${isExpanded ? 'chevron-down' : 'chevron-right'}`}></i>
									</span>
								) : (
									<span className="categoria-icon">
										<i className="fa fa-tag"></i>
									</span>
								)}
							</div>

							{/* Nombre de la categoría */}
							<span className={`categoria-nombre ${level === 0 ? "fw-bold" : ""}`} style={{ color: level === 0 ? levelColor : 'inherit' }}>
								{category.nombre}
							</span>
						</div>
					</td>

					{/* Imagen - Usando un div con fondo en vez de una img para mayor estabilidad */}
					<td className="text-center imagen-cell">
						<div className="imagen-container">
							<img
								src={category.multimedia ? `${API_URL}${category.multimedia.url}` : CATEGORIAS_NO_IMAGE}
								alt={category.nombre || 'Imagen de categoría'}
								className="categoria-imagen"
							/>
						</div>
					</td>

					{/* Orden - Con indicador de carga si la categoría está siendo actualizada */}
					<td className="text-center orden-cell">
						{categoriasActualizando.includes(category.id) ? (
							<div className="spinner-border spinner-border-sm text-secondary" role="status">
								<span className="visually-hidden">Actualizando...</span>
							</div>
						) : (
							<span className="badge bg-secondary">{category.orden}</span>
						)}
					</td>

					{/* Ruta de categoría */}
					<td className="ruta-cell">
						{level === 0 ? (
							<span className="badge bg-primary">Categoría principal</span>
						) : (
							<div className="categoria-ruta">
								{currentPath.slice(0, -1).map((name, i) => (
									<React.Fragment key={i}>
										<span
											className="categoria-ruta-item"
											style={{ color: getLevelColor(i) }}
										>
											{name}
										</span>
										{i < currentPath.length - 2 && <i className="fa fa-angle-right mx-1 text-secondary"></i>}
									</React.Fragment>
								))}
							</div>
						)}
					</td>

					{/* Subcategorías */}
					<td className="text-center subcategorias-cell">
						{hasSubcategories ? (
							<span className="badge bg-info rounded-pill">{category.subcategorias.length}</span>
						) : (
							<span className="text-muted">-</span>
						)}
					</td>

					{/* Acciones */}
					<td className="acciones-cell">
						<DropdownButton
							as={ButtonGroup}
							title="OPCIONES"
							id={`bg-nested-dropdown-${category.id}`}
							className="fst-normal"
							size="sm"
							disabled={categoriasActualizando.length > 0}
						>
							<Dropdown.Item
								onClick={() => acciones.handleVer(category)}
								eventKey="ver"
							>
								Ver
							</Dropdown.Item>
							<Dropdown.Item
								onClick={() => acciones.handleEditar(category)}
								eventKey="editar"
							>
								Editar
							</Dropdown.Item>
							<Dropdown.Item
								onClick={() => acciones.handleEliminar(category)}
								eventKey="eliminar"
							>
								Eliminar
							</Dropdown.Item>
						</DropdownButton>
					</td>
				</tr>

				{/* Renderizar subcategorías si están expandidas */}
				{isExpanded && hasSubcategories && category.subcategorias.map(subcategory =>
					renderCategory(subcategory, level + 1, currentPath)
				)}
			</React.Fragment>
		)
	}, [expandedCategories, draggedItem, dragOverItem, categoriasActualizando, handleDragStart, handleDragOver, handleDragLeave, handleDragEnd, handleDrop, toggleExpand])

	return (
		<div className="lista-categorias">
			<Row>
				<Col>
					<small>Total resultados: {loading ? (<span className="ms-2"><LoadingSpinner size='sm' /></span>) : (<strong>{numResultados}</strong>)}</small>
				</Col>
			</Row>
			<Row>
				<Col>
					{loading ? (
						<div className="text-center my-4">
							<LoadingSpinner />
						</div>
					) : (
						<>
							<div className="alert alert-info my-2">
								<i className="fa fa-info-circle me-2"></i>
								Puedes arrastrar las categorías para reordenarlas. Haz clic en los iconos <i className="fa fa-chevron-right"></i> para expandir o contraer subcategorías.
							</div>
							<Table id="tabla-categorias" striped bordered hover size="sm" className="tabla-categorias">
								<thead>
									<tr>
										<th className="drag-handle-header"></th>
										<th className="id-header">ID</th>
										<th className="nombre-header">Nombre</th>
										<th className="imagen-header">Imagen</th>
										<th className="orden-header">Orden</th>
										<th className="ruta-header">Ruta</th>
										<th className="subcategorias-header"># Sub.</th>
										<th className="acciones-header">Acciones</th>
									</tr>
								</thead>
								<tbody>
									{categorias.length ? (
										categorias.map(category => renderCategory(category))
									) : (
										<tr>
											<td colSpan="8" className="text-center">
												No se encontraron categorías.
											</td>
										</tr>
									)}
								</tbody>
							</Table>
						</>
					)}
				</Col>
			</Row>

			<Row>
				<Col>
					<Paginacion
						pagina={pagina}
						numPaginas={Math.ceil(numResultados / fetchDeACuantos) || 1}
						setPagina={cambiarPagina}
						disabled={categoriasActualizando.length > 0}
					/>
				</Col>
			</Row>
		</div>
	)
}